import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Modal } from '../components/Modal';
import { DependentChips } from '../components/DependentChips';
import { DependentBadge, DEPENDENT_COLORS } from '../components/DependentBadge';
import { CreateTaskForm } from '../components/CreateTaskForm';
import { CreateAppointmentForm } from '../components/CreateAppointmentForm';
import { ActivityForm } from '../components/ActivityForm';
import { AttendeeSelect } from '../components/AttendeeSelect';
import type { AttendeeState } from '../components/AttendeeSelect';
import { RecurrenceSelect } from '../components/RecurrenceSelect';
import { FormField } from '../components/FormField';
import type { CalendarEvent, RecurrenceRule, ActivityCategory } from '@kinapp/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type CalendarView = 'day' | 'week' | 'month' | 'agenda';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const HOUR_START = 7;
const HOUR_END = 22;
const HOUR_HEIGHT = 64; // px per hour, 1px per minute ≈ 64/60
const PX_PER_MIN = HOUR_HEIGHT / 60;
const TOTAL_HEIGHT = (HOUR_END - HOUR_START) * HOUR_HEIGHT;

const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const CATEGORY_BORDER: Record<string, string> = {
  SPORT: 'border-l-green-500',
  MUSIC: 'border-l-purple-500',
  ACADEMIC: 'border-l-blue-500',
  SOCIAL: 'border-l-pink-500',
  MEDICAL: 'border-l-red-500',
  TRANSPORT: 'border-l-amber-500',
  OTHER: 'border-l-gray-500',
};

const TYPE_STYLE: Record<CalendarEvent['type'], string> = {
  ACTIVITY: 'bg-gray-50',
  APPOINTMENT: 'bg-blue-50 border-l-blue-500',
  TASK: 'bg-amber-50 border-l-amber-400',
  CUSTODY: 'bg-purple-50 border-l-purple-400 border-dashed',
  MEDICATION: 'bg-green-50 border-l-green-500',
};

const TYPE_LABEL: Record<CalendarEvent['type'], string> = {
  ACTIVITY: 'Activity',
  APPOINTMENT: 'Appointment',
  TASK: 'Task',
  CUSTODY: 'Handover',
  MEDICATION: 'Medication',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function isoDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function timeToPx(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h - HOUR_START) * HOUR_HEIGHT + m * PX_PER_MIN;
}

function durationPx(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const minutes = (eh * 60 + em) - (sh * 60 + sm);
  return Math.max(minutes * PX_PER_MIN, 28);
}

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
}

function dayOfWeekName(date: Date): string {
  return DAY_NAMES[((date.getDay() + 6) % 7)];
}

function formatWeekRange(start: Date): string {
  const end = addDays(start, 6);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })} – ${end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${start.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
  }
  return `${start.getDate()} – ${end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// EventCard
// ─────────────────────────────────────────────────────────────────────────────

interface EventCardProps {
  event: CalendarEvent;
  onDragStart: () => void;
  onClick: () => void;
}

function EventCard({ event, onDragStart, onClick }: EventCardProps) {
  const isActivity = event.type === 'ACTIVITY';
  const typeStyle = isActivity
    ? `bg-gray-50 ${CATEGORY_BORDER[event.category ?? 'OTHER'] ?? 'border-l-gray-500'}`
    : TYPE_STYLE[event.type];

  const isPending = event.approvalStatus === 'PENDING';
  const isDeclined = event.approvalStatus === 'DECLINED';

  return (
    <div
      draggable={event.isDraggable && !isPending}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onClick={onClick}
      className={`absolute left-0.5 right-0.5 rounded border-l-2 ${typeStyle} px-1.5 py-1 text-xs cursor-pointer hover:brightness-95 active:brightness-90 overflow-hidden select-none ${isDeclined ? 'opacity-50' : ''}`}
      style={{
        top: timeToPx(event.startTime!),
        height: durationPx(event.startTime!, event.endTime!),
      }}
      title={`${event.title}${event.venue ? ` @ ${event.venue}` : ''} (${event.startTime}–${event.endTime})${isPending ? ' — Pending guardian approval' : ''}`}
    >
      <div className="flex items-center gap-1 leading-tight">
        <span className="font-medium text-gray-900 truncate flex-1">{event.title}</span>
        {isPending && (
          <span className="shrink-0 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wide">
            Pending
          </span>
        )}
        {isDeclined && (
          <span className="shrink-0 text-[9px] font-semibold px-1 py-0.5 rounded bg-red-100 text-red-700 uppercase tracking-wide">
            Declined
          </span>
        )}
      </div>
      <div className="text-gray-500 truncate">{event.startTime}</div>
      <div className="flex gap-0.5 mt-0.5">
        {event.dependentIds.map((id, i) => (
          <DependentBadge key={id} name={event.dependentNames[i] ?? ''} colorIndex={event.colorIndex + i} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AllDayChip
// ─────────────────────────────────────────────────────────────────────────────

function AllDayChip({ event, onClick }: { event: CalendarEvent; onClick: () => void }) {
  const isPending = event.approvalStatus === 'PENDING';
  const isDeclined = event.approvalStatus === 'DECLINED';
  return (
    <div
      onClick={onClick}
      className={`rounded border-l-2 ${TYPE_STYLE[event.type]} px-1.5 py-0.5 text-xs cursor-pointer hover:brightness-95 flex items-center gap-1 ${isDeclined ? 'opacity-50' : ''}`}
      title={`${event.title}${isPending ? ' — Pending guardian approval' : ''}`}
    >
      <span className="truncate flex-1">{event.title}</span>
      {isPending && (
        <span className="shrink-0 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wide">
          Pending
        </span>
      )}
      {isDeclined && (
        <span className="shrink-0 text-[9px] font-semibold px-1 py-0.5 rounded bg-red-100 text-red-700 uppercase tracking-wide">
          Declined
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EventDetailPanel — slide-in panel (matches CreateEventPanel pattern)
// ─────────────────────────────────────────────────────────────────────────────

interface EventDetailPanelProps {
  event: CalendarEvent;
  familyId: string;
  familyMembers: Array<{ user: { id: string; name: string }; role: string }>;
  dependents: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDelete: () => void;
  onSave: () => void;
}

const ACTIVITY_CATEGORIES: { value: ActivityCategory; label: string }[] = [
  { value: 'SPORT' as ActivityCategory, label: 'Sport' },
  { value: 'MUSIC' as ActivityCategory, label: 'Music' },
  { value: 'ACADEMIC' as ActivityCategory, label: 'Academic' },
  { value: 'SOCIAL' as ActivityCategory, label: 'Social' },
  { value: 'MEDICAL' as ActivityCategory, label: 'Medical' },
  { value: 'TRANSPORT' as ActivityCategory, label: 'Transport' },
  { value: 'OTHER' as ActivityCategory, label: 'Other' },
];

function EventDetailPanel({ event, familyId, familyMembers, dependents, onClose, onDelete, onSave }: EventDetailPanelProps) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const hasRecurrence = !!(event.recurrenceRule);
  const showScopeSelector = event.type === 'ACTIVITY' || (hasRecurrence && event.type !== 'CUSTODY' && event.type !== 'MEDICATION');

  // Default "This session" for activities, "All occurrences" for APPOINTMENT/TASK
  const [editScope, setEditScope] = useState<'THIS' | 'ALL'>(
    event.type === 'ACTIVITY' ? 'THIS' : 'ALL'
  );

  // ── Session state (ACTIVITY "this session") ──────────────────────────────
  const [sessionStart, setSessionStart] = useState(event.startTime ?? '');
  const [sessionEnd, setSessionEnd] = useState(event.endTime ?? '');

  // ── APPOINTMENT state ────────────────────────────────────────────────────
  const [apptTitle, setApptTitle] = useState(event.title);
  const [apptDateTime, setApptDateTime] = useState(
    event.date && event.startTime ? `${event.date}T${event.startTime}` : event.date
  );
  const [apptLocation, setApptLocation] = useState(event.venue ?? '');
  const [apptNotes, setApptNotes] = useState(event.notes ?? '');
  const [apptRecurrence, setApptRecurrence] = useState<RecurrenceRule | null>(
    (event.recurrenceRule as RecurrenceRule) ?? null
  );
  const [apptAttendees, setApptAttendees] = useState<AttendeeState>({
    selectedUserIds: event.attendingUserIds ?? [],
    selectedDepIds: event.dependentIds ?? [],
    externalGuests: event.externalGuests ?? [],
  });

  // ── TASK state ───────────────────────────────────────────────────────────
  const [taskTitle, setTaskTitle] = useState(event.title);
  const [taskDueAt, setTaskDueAt] = useState(event.date);
  const [taskNotes, setTaskNotes] = useState(event.notes ?? '');
  const [taskAssignedToId, setTaskAssignedToId] = useState(event.assignedToId ?? '');
  const [taskRecurrence, setTaskRecurrence] = useState<RecurrenceRule | null>(
    (event.recurrenceRule as RecurrenceRule) ?? null
  );

  // ── ACTIVITY state (all sessions) ────────────────────────────────────────
  const [actName, setActName] = useState(event.title);
  const [actCategory, setActCategory] = useState<ActivityCategory>(
    (event.category as ActivityCategory) ?? 'SPORT'
  );
  const [actVenue, setActVenue] = useState(event.venue ?? '');
  const [actNotes, setActNotes] = useState(event.notes ?? '');
  const [actAttendees, setActAttendees] = useState<AttendeeState>({
    selectedUserIds: event.attendingUserIds ?? [],
    selectedDepIds: event.dependentIds ?? [],
    externalGuests: event.externalGuests ?? [],
  });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (event.type === 'ACTIVITY') {
        if (editScope === 'THIS') {
          // Edit only this session's time
          await api.updateActivitySession(familyId, event.sourceId, event.sessionId!, {
            startTime: sessionStart || undefined,
            endTime: sessionEnd || undefined,
          });
        } else {
          // Edit all sessions (activity-wide fields)
          await api.updateScheduledActivity(familyId, event.sourceId, {
            name: actName.trim() || undefined,
            category: actCategory,
            venue: actVenue.trim() || undefined,
            notes: actNotes.trim() || undefined,
            attendingUserIds: actAttendees.selectedUserIds,
            externalGuests: actAttendees.externalGuests.length ? actAttendees.externalGuests : undefined,
          });
        }
      } else if (event.type === 'APPOINTMENT') {
        if (editScope === 'THIS') {
          // Clone as one-off for this specific date
          await api.createAppointmentException(event.sourceId, {
            originalDate: event.date,
            title: apptTitle.trim() || undefined,
            dateTime: apptDateTime ? new Date(apptDateTime).toISOString() : undefined,
            location: apptLocation.trim() || undefined,
            notes: apptNotes.trim() || undefined,
            attendingUserIds: apptAttendees.selectedUserIds,
            externalGuests: apptAttendees.externalGuests.length ? apptAttendees.externalGuests : undefined,
          });
        } else {
          // Update all occurrences (base event)
          await api.updateAppointment(event.sourceId, {
            title: apptTitle.trim() || undefined,
            dateTime: apptDateTime ? new Date(apptDateTime).toISOString() : undefined,
            location: apptLocation.trim() || undefined,
            notes: apptNotes.trim() || undefined,
            attendingUserIds: apptAttendees.selectedUserIds,
            externalGuests: apptAttendees.externalGuests.length ? apptAttendees.externalGuests : undefined,
            recurrenceRule: apptRecurrence ?? null,
          });
        }
      } else if (event.type === 'TASK') {
        if (editScope === 'THIS') {
          // Clone as one-off for this specific date
          await api.createTaskException(event.sourceId, {
            originalDate: event.date,
            title: taskTitle.trim() || undefined,
            dueAt: taskDueAt ? new Date(taskDueAt + 'T00:00:00').toISOString() : undefined,
            description: taskNotes.trim() || undefined,
            assignedToId: taskAssignedToId || null,
          });
        } else {
          // Update all occurrences (base event)
          await api.updateTask(event.sourceId, {
            title: taskTitle.trim() || undefined,
            dueAt: taskDueAt ? new Date(taskDueAt + 'T00:00:00').toISOString() : undefined,
            description: taskNotes.trim() || undefined,
            assignedToId: taskAssignedToId || null,
            recurrenceRule: taskRecurrence ?? null,
          });
        }
      }
      onSave();
    } catch {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${event.title}"?`)) return;
    setDeleting(true);
    try {
      if (event.type === 'ACTIVITY') {
        await api.deleteScheduledActivity(familyId, event.sourceId);
      } else if (event.type === 'APPOINTMENT') {
        await api.deleteAppointment(event.sourceId);
      } else if (event.type === 'TASK') {
        await api.deleteTask(event.sourceId);
      }
      onDelete();
    } catch {
      setError('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkDone = async () => {
    setSaving(true);
    try {
      await api.updateTask(event.sourceId, { status: 'DONE' });
      onDelete();
    } catch {
      setError('Failed to mark done');
    } finally {
      setSaving(false);
    }
  };

  const scopeLabels = event.type === 'ACTIVITY'
    ? { THIS: 'This session', ALL: 'All sessions' }
    : { THIS: 'This occurrence', ALL: 'All occurrences' };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Slide-in panel */}
      <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full sm:max-w-md bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{TYPE_LABEL[event.type]}</p>
            <h2 className="text-base font-semibold text-gray-900 truncate">{event.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scope selector — shown for activities (always) and for recurring appointments/tasks */}
        {showScopeSelector && (
          <div className="px-4 py-2.5 border-b border-gray-100 shrink-0">
            <div className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
              {(['THIS', 'ALL'] as const).map((scope) => (
                <button
                  key={scope}
                  onClick={() => setEditScope(scope)}
                  className={`flex-1 py-1.5 text-sm rounded-md transition-colors ${
                    editScope === scope
                      ? 'bg-white shadow-sm font-medium text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {scopeLabels[scope]}
                </button>
              ))}
            </div>
            {editScope === 'ALL' && hasRecurrence && (
              <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Changes apply to all occurrences of this recurring event
              </p>
            )}
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* ── APPOINTMENT ── */}
          {event.type === 'APPOINTMENT' && (
            <>
              <AttendeeSelect
                dependents={dependents}
                familyMembers={familyMembers as any}
                value={apptAttendees}
                onChange={setApptAttendees}
              />
              <FormField label="Title" id="edit-appt-title" required>
                <input
                  type="text"
                  id="edit-appt-title"
                  value={apptTitle}
                  onChange={(e) => setApptTitle(e.target.value)}
                  className="input"
                />
              </FormField>
              <FormField label="Date & time" id="edit-appt-dt">
                <input
                  type="datetime-local"
                  id="edit-appt-dt"
                  value={apptDateTime}
                  onChange={(e) => setApptDateTime(e.target.value)}
                  className="input"
                />
              </FormField>
              <FormField label="Location" id="edit-appt-loc">
                <input
                  type="text"
                  id="edit-appt-loc"
                  value={apptLocation}
                  onChange={(e) => setApptLocation(e.target.value)}
                  className="input"
                  placeholder="e.g. GP Surgery, Hospital"
                />
              </FormField>
              {/* Only show recurrence selector when editing all occurrences */}
              {editScope === 'ALL' && (
                <FormField label="Recurrence" id="edit-appt-rec">
                  <RecurrenceSelect
                    value={apptRecurrence}
                    onChange={setApptRecurrence}
                    referenceDate={event.date}
                  />
                </FormField>
              )}
              <FormField label="Notes" id="edit-appt-notes">
                <textarea
                  id="edit-appt-notes"
                  value={apptNotes}
                  onChange={(e) => setApptNotes(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Any additional notes…"
                />
              </FormField>
            </>
          )}

          {/* ── TASK ── */}
          {event.type === 'TASK' && (
            <>
              {/* Read-only dependent chips */}
              {event.dependentIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {event.dependentIds.map((id, i) => (
                    <span
                      key={id}
                      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${DEPENDENT_COLORS[(event.colorIndex + i) % DEPENDENT_COLORS.length]?.bg ?? 'bg-gray-100'} ${DEPENDENT_COLORS[(event.colorIndex + i) % DEPENDENT_COLORS.length]?.text ?? 'text-gray-700'}`}
                    >
                      {event.dependentNames[i]}
                    </span>
                  ))}
                </div>
              )}
              <FormField label="Title" id="edit-task-title" required>
                <input
                  type="text"
                  id="edit-task-title"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="input"
                />
              </FormField>
              <FormField label="Due date" id="edit-task-due">
                <input
                  type="date"
                  id="edit-task-due"
                  value={taskDueAt}
                  onChange={(e) => setTaskDueAt(e.target.value)}
                  className="input"
                />
              </FormField>
              <FormField label="Assigned to" id="edit-task-assigned">
                <select
                  id="edit-task-assigned"
                  value={taskAssignedToId}
                  onChange={(e) => setTaskAssignedToId(e.target.value)}
                  className="input"
                >
                  <option value="">Unassigned</option>
                  {familyMembers.map(({ user }: { user: { id: string; name: string } }) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </FormField>
              {/* Only show recurrence selector when editing all occurrences */}
              {editScope === 'ALL' && (
                <FormField label="Recurrence" id="edit-task-rec">
                  <RecurrenceSelect
                    value={taskRecurrence}
                    onChange={setTaskRecurrence}
                    referenceDate={event.date}
                  />
                </FormField>
              )}
              <FormField label="Notes" id="edit-task-notes">
                <textarea
                  id="edit-task-notes"
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Any additional notes…"
                />
              </FormField>
            </>
          )}

          {/* ── ACTIVITY ── */}
          {event.type === 'ACTIVITY' && editScope === 'THIS' && (
            <>
              <p className="text-sm text-gray-600">
                Change the time for <span className="font-medium">{event.date}</span>'s session only.
              </p>
              <div className="flex gap-3 items-center">
                <FormField label="Start time" id="edit-sess-start">
                  <input
                    type="time"
                    id="edit-sess-start"
                    value={sessionStart}
                    onChange={(e) => setSessionStart(e.target.value)}
                    className="input"
                  />
                </FormField>
                <span className="text-gray-400 mt-6">to</span>
                <FormField label="End time" id="edit-sess-end">
                  <input
                    type="time"
                    id="edit-sess-end"
                    value={sessionEnd}
                    onChange={(e) => setSessionEnd(e.target.value)}
                    className="input"
                  />
                </FormField>
              </div>
            </>
          )}

          {event.type === 'ACTIVITY' && editScope === 'ALL' && (
            <>
              <AttendeeSelect
                dependents={dependents}
                familyMembers={familyMembers as any}
                value={actAttendees}
                onChange={setActAttendees}
              />
              <FormField label="Name" id="edit-act-name" required>
                <input
                  type="text"
                  id="edit-act-name"
                  value={actName}
                  onChange={(e) => setActName(e.target.value)}
                  className="input"
                />
              </FormField>
              <FormField label="Category" id="edit-act-cat">
                <select
                  id="edit-act-cat"
                  value={actCategory}
                  onChange={(e) => setActCategory(e.target.value as ActivityCategory)}
                  className="input"
                >
                  {ACTIVITY_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Venue" id="edit-act-venue">
                <input
                  type="text"
                  id="edit-act-venue"
                  value={actVenue}
                  onChange={(e) => setActVenue(e.target.value)}
                  className="input"
                  placeholder="e.g. Lincoln Park Sports Center"
                />
              </FormField>
              <FormField label="Notes" id="edit-act-notes">
                <textarea
                  id="edit-act-notes"
                  value={actNotes}
                  onChange={(e) => setActNotes(e.target.value)}
                  className="input"
                  rows={2}
                  placeholder="Any additional notes…"
                />
              </FormField>
            </>
          )}

          {/* ── CUSTODY: read-only ── */}
          {event.type === 'CUSTODY' && (
            <div className="text-sm text-gray-700 space-y-1">
              <p>{event.title}</p>
              {event.startTime && <p>Time: {event.startTime}</p>}
            </div>
          )}

          {/* ── MEDICATION: read-only ── */}
          {event.type === 'MEDICATION' && (
            <div className="text-sm text-gray-700 space-y-2">
              <div className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.7-1.5 2.7H4.298c-1.336 0-2.35-1.26-1.809-2.49l.392-.98" />
                  </svg>
                </span>
                <div>
                  <p className="font-medium text-gray-900">{event.title}</p>
                  {event.dose && <p className="text-gray-500 text-xs mt-0.5">{event.dose}</p>}
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Manage this medication from the medications section of your dashboard.
              </p>
            </div>
          )}
        </div>

        {/* Sticky footer actions */}
        <div className="shrink-0 px-4 py-4 border-t border-gray-200 flex gap-2">
          {event.isEditable && event.type !== 'CUSTODY' && event.type !== 'MEDICATION' && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {event.type === 'TASK' && (
            <button
              onClick={handleMarkDone}
              disabled={saving}
              className="flex-1 btn btn-secondary"
            >
              Mark done
            </button>
          )}
          {event.isEditable && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 btn bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button onClick={onClose} className="flex-1 btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CreateEventPanel — slide-in panel replacing the two-step modal chain
// ─────────────────────────────────────────────────────────────────────────────

type CreateType = 'APPOINTMENT' | 'TASK' | 'ACTIVITY';

const DOW_FROM_JS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

function slotDayOfWeek(dateStr: string): string {
  // Parse as local date to avoid UTC offset shifting the day
  const [y, mo, d] = dateStr.split('-').map(Number);
  return DOW_FROM_JS[new Date(y, mo - 1, d).getDay()];
}

interface CreateEventPanelProps {
  slot: { date: string; time: string };
  familyId: string;
  familyMembers: Array<{ user: { id: string; name: string }; role: string }>;
  dependents: Array<{ id: string; name: string }>;
  onSuccess: () => void;
  onClose: () => void;
}

function CreateEventPanel({
  slot,
  familyId,
  familyMembers,
  dependents,
  onSuccess,
  onClose,
}: CreateEventPanelProps) {
  const [type, setType] = useState<CreateType>('APPOINTMENT');

  // Pre-computed initial values for each form
  const initialDateTime = slot.time ? `${slot.date}T${slot.time}` : '';
  const initialDueAt = `${slot.date}T${slot.time || '12:00'}`;
  const initialDayOfWeek = slotDayOfWeek(slot.date) as any;
  const initialStartTime = slot.time || '09:00';

  const contextLabel = (() => {
    const [y, mo, d] = slot.date.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  })();

  const tabs: [CreateType, string, string][] = [
    ['APPOINTMENT', 'Appointment', 'One-off visit or event'],
    ['TASK', 'Task', 'Something to do or remember'],
    ['ACTIVITY', 'Activity', 'Weekly recurring session'],
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full sm:max-w-md bg-white shadow-xl">
        {/* Header: type tabs + close */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
          <div className="flex gap-0.5 flex-1 rounded-lg bg-gray-100 p-0.5">
            {tabs.map(([t, label]) => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`flex-1 py-1.5 text-sm rounded-md transition-colors ${
                  type === t
                    ? 'bg-white shadow-sm font-medium text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Context bar — shows the clicked date/time */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 shrink-0">
          <p className="text-xs text-gray-500">
            {contextLabel}
            {slot.time && (
              <span className="ml-1 font-medium text-gray-700">· {slot.time}</span>
            )}
            <span className="ml-1 text-gray-400">
              — {tabs.find(([t]) => t === type)?.[2]}
            </span>
          </p>
        </div>

        {/* Scrollable form body */}
        <div className="flex-1 overflow-y-auto px-4 py-5">
          {type === 'APPOINTMENT' && (
            <CreateAppointmentForm
              familyId={familyId}
              dependents={dependents}
              familyMembers={familyMembers as any}
              onSuccess={onSuccess}
              onCancel={onClose}
              initialDateTime={initialDateTime}
            />
          )}
          {type === 'TASK' && (
            <CreateTaskForm
              familyId={familyId}
              dependents={dependents}
              familyMembers={familyMembers as any}
              onSuccess={onSuccess}
              onCancel={onClose}
              initialDueAt={initialDueAt}
            />
          )}
          {type === 'ACTIVITY' && (
            <ActivityForm
              familyId={familyId}
              dependents={dependents}
              familyMembers={familyMembers as any}
              onSuccess={onSuccess}
              onCancel={onClose}
              initialDayOfWeek={initialDayOfWeek}
              initialStartTime={initialStartTime}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SubscribeModal — iCal subscription URL manager
// ─────────────────────────────────────────────────────────────────────────────

interface SubscribeModalProps {
  familyId: string;
  onClose: () => void;
}

function SubscribeModal({ familyId, onClose }: SubscribeModalProps) {
  const [tokens, setTokens] = useState<Array<{ id: string; token: string; label?: string; dependentIds: string[]; includeTypes: string[]; createdAt: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const loadTokens = async () => {
    try {
      const data = await api.getCalendarTokens(familyId);
      setTokens(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTokens(); }, [familyId]);

  const createToken = async () => {
    setCreating(true);
    try {
      await api.createCalendarToken(familyId, { label: 'All events' });
      await loadTokens();
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async (tokenId: string) => {
    await api.deleteCalendarToken(familyId, tokenId);
    setTokens((prev) => prev.filter((t) => t.id !== tokenId));
  };

  const buildUrl = (token: string) => {
    const base = window.location.origin.replace(/^http/, 'webcal');
    return `${base}/ical/${token}`;
  };

  const copyUrl = async (token: string) => {
    const url = buildUrl(token);
    await navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Modal isOpen title="Subscribe to Calendar" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-4">
        Add your Kin calendar to Apple Calendar, Google Calendar, or any app that supports
        iCal (webcal) subscriptions.
      </p>

      {loading ? (
        <div className="text-center py-6 text-gray-400 text-sm">Loading…</div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-3">No subscriptions yet.</p>
          <button
            onClick={createToken}
            disabled={creating}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create subscription link'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tokens.map((t) => {
            const url = buildUrl(t.token);
            const isCopied = copied === t.token;
            return (
              <div key={t.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {t.label ?? 'All events'}
                  </span>
                  <button
                    onClick={() => revokeToken(t.id)}
                    className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                    title="Revoke this subscription"
                  >
                    Revoke
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] text-gray-500 bg-gray-50 rounded px-2 py-1 truncate">
                    {url}
                  </code>
                  <button
                    onClick={() => copyUrl(t.token)}
                    className={`shrink-0 text-xs px-2.5 py-1 rounded border transition-colors ${
                      isCopied
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {isCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <a
                    href={url}
                    className="shrink-0 text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                    title="Open in Calendar app"
                  >
                    Open
                  </a>
                </div>
              </div>
            );
          })}

          <button
            onClick={createToken}
            disabled={creating}
            className="w-full text-sm text-primary-600 hover:text-primary-700 py-1 disabled:opacity-50"
          >
            {creating ? 'Creating…' : '+ Add another subscription'}
          </button>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MonthView (inline)
// ─────────────────────────────────────────────────────────────────────────────

interface MonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  todayStr: string;
  onEventClick: (ev: CalendarEvent) => void;
  onDayClick: (date: Date) => void;
}

function MonthView({ currentDate, events, todayStr, onEventClick, onDayClick }: MonthViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  // Start grid from Monday of the week containing monthStart
  const gridStart = getMonday(monthStart);
  // End grid: enough rows to cover all days
  const totalDays = Math.ceil((monthEnd.getTime() - gridStart.getTime()) / (86400000)) + 7;
  const totalRows = Math.ceil(totalDays / 7);
  const gridDays = Array.from({ length: totalRows * 7 }, (_, i) => addDays(gridStart, i));

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((ev) => {
      const list = map.get(ev.date) ?? [];
      list.push(ev);
      map.set(ev.date, list);
    });
    return map;
  }, [events]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Day-of-week header */}
      <div className="grid border-b border-gray-200" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {DAY_LABELS.map((label) => (
          <div key={label} className="text-center py-2 text-xs font-medium text-gray-500 border-r border-gray-100 last:border-r-0">
            {label}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {gridDays.map((day, idx) => {
          const dateStr = isoDate(day);
          const isThisMonth = day.getMonth() === currentDate.getMonth();
          const isToday = dateStr === todayStr;
          const dayEvents = eventsByDate.get(dateStr) ?? [];
          const visibleEvents = dayEvents.slice(0, 3);
          const overflow = dayEvents.length - 3;

          return (
            <div
              key={dateStr}
              className={`min-h-[90px] border-r border-b border-gray-100 last:border-r-0 p-1 ${
                isThisMonth ? 'bg-white' : 'bg-gray-50'
              } ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}`}
            >
              {/* Day number */}
              <button
                onClick={() => onDayClick(day)}
                className={`w-6 h-6 text-xs font-medium rounded-full flex items-center justify-center mb-1 ${
                  isToday
                    ? 'bg-primary-600 text-white'
                    : isThisMonth
                    ? 'text-gray-900 hover:bg-gray-100'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                {day.getDate()}
              </button>

              {/* Event chips */}
              <div className="space-y-0.5">
                {visibleEvents.map((ev) => {
                  const isActivity = ev.type === 'ACTIVITY';
                  const chipStyle = isActivity
                    ? `bg-gray-100 border-l-2 ${CATEGORY_BORDER[ev.category ?? 'OTHER'] ?? 'border-l-gray-400'}`
                    : ev.type === 'APPOINTMENT'
                    ? 'bg-blue-50 border-l-2 border-l-blue-500'
                    : ev.type === 'TASK'
                    ? 'bg-amber-50 border-l-2 border-l-amber-400'
                    : ev.type === 'MEDICATION'
                    ? 'bg-green-50 border-l-2 border-l-green-500'
                    : 'bg-purple-50 border-l-2 border-l-purple-400';
                  return (
                    <button
                      key={ev.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                      className={`w-full text-left text-[10px] px-1 py-0.5 rounded truncate ${chipStyle} hover:brightness-95`}
                      title={ev.title}
                    >
                      {!ev.allDay && ev.startTime && (
                        <span className="text-gray-400 mr-0.5">{ev.startTime}</span>
                      )}
                      {ev.title}
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <button
                    onClick={() => onDayClick(day)}
                    className="w-full text-left text-[10px] text-gray-400 hover:text-gray-600 px-1"
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AgendaView (inline)
// ─────────────────────────────────────────────────────────────────────────────

interface AgendaViewProps {
  events: CalendarEvent[];
  rangeStart: Date;
  rangeEnd: Date;
  todayStr: string;
  onEventClick: (ev: CalendarEvent) => void;
}

function AgendaView({ events, rangeStart, rangeEnd, todayStr, onEventClick }: AgendaViewProps) {
  // Build a list of all days in range
  const days: Date[] = [];
  let cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    days.push(new Date(cursor));
    cursor = addDays(cursor, 1);
  }

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((ev) => {
      const list = map.get(ev.date) ?? [];
      list.push(ev);
      map.set(ev.date, list);
    });
    return map;
  }, [events]);

  const daysWithEvents = days.filter((d) => (eventsByDate.get(isoDate(d)) ?? []).length > 0);

  if (daysWithEvents.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <p className="text-gray-400 text-sm">No events in this period</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
      {daysWithEvents.map((day) => {
        const dateStr = isoDate(day);
        const isToday = dateStr === todayStr;
        const dayEvents = (eventsByDate.get(dateStr) ?? []).sort((a, b) => {
          if (a.allDay && !b.allDay) return -1;
          if (!a.allDay && b.allDay) return 1;
          return (a.startTime ?? '').localeCompare(b.startTime ?? '');
        });

        return (
          <div key={dateStr} className="flex">
            {/* Date label */}
            <div className={`w-20 shrink-0 p-3 ${isToday ? 'text-primary-700' : 'text-gray-500'}`}>
              <div className="text-xs font-medium uppercase tracking-wide">
                {day.toLocaleDateString('en-GB', { weekday: 'short' })}
              </div>
              <div className={`text-xl font-semibold leading-tight ${isToday ? 'text-primary-600' : 'text-gray-900'}`}>
                {day.getDate()}
              </div>
              <div className="text-[11px] text-gray-400">
                {day.toLocaleDateString('en-GB', { month: 'short' })}
              </div>
            </div>

            {/* Events */}
            <div className="flex-1 py-2 pr-3 space-y-1.5">
              {dayEvents.map((ev) => {
                const isActivity = ev.type === 'ACTIVITY';
                const borderStyle = isActivity
                  ? CATEGORY_BORDER[ev.category ?? 'OTHER'] ?? 'border-l-gray-400'
                  : ev.type === 'APPOINTMENT'
                  ? 'border-l-blue-500'
                  : ev.type === 'TASK'
                  ? 'border-l-amber-400'
                  : ev.type === 'MEDICATION'
                  ? 'border-l-green-500'
                  : 'border-l-purple-400';
                const bgStyle = isActivity
                  ? 'bg-gray-50'
                  : ev.type === 'APPOINTMENT'
                  ? 'bg-blue-50'
                  : ev.type === 'TASK'
                  ? 'bg-amber-50'
                  : ev.type === 'MEDICATION'
                  ? 'bg-green-50'
                  : 'bg-purple-50';
                const isPending = ev.approvalStatus === 'PENDING';
                const isDeclined = ev.approvalStatus === 'DECLINED';

                return (
                  <button
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded border-l-2 ${bgStyle} ${borderStyle} hover:brightness-95 ${isDeclined ? 'opacity-50' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{ev.title}</div>
                      <div className="text-xs text-gray-500">
                        {ev.allDay ? (
                          <span>All day</span>
                        ) : (
                          <span>{ev.startTime}{ev.endTime ? ` – ${ev.endTime}` : ''}</span>
                        )}
                        {ev.venue && <span> · {ev.venue}</span>}
                        {ev.dependentNames.length > 0 && (
                          <span> · {ev.dependentNames.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    {isPending && (
                      <span className="shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wide">
                        Pending
                      </span>
                    )}
                    {isDeclined && (
                      <span className="shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 uppercase tracking-wide">
                        Declined
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main CalendarPage
// ─────────────────────────────────────────────────────────────────────────────

export function CalendarPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<CalendarView>('week');
  const [currentDate, setCurrentDate] = useState<Date>(() => getMonday(new Date()));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [dependents, setDependents] = useState<Array<{ id: string; name: string }>>([]);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  const [activeDepIds, setActiveDepIds] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [newSlot, setNewSlot] = useState<{ date: string; time: string } | null>(null);

  const [showSubscribe, setShowSubscribe] = useState(false);

  const draggedEvent = useRef<CalendarEvent | null>(null);

  // ── Compute date range from view ──────────────────────────────────────────

  const { rangeStart, rangeEnd } = useMemo(() => {
    switch (view) {
      case 'day':
        return { rangeStart: currentDate, rangeEnd: currentDate };
      case 'week':
        return { rangeStart: currentDate, rangeEnd: addDays(currentDate, 6) };
      case 'month': {
        const ms = startOfMonth(currentDate);
        const me = endOfMonth(currentDate);
        return { rangeStart: getMonday(ms), rangeEnd: addDays(me, 6) };
      }
      case 'agenda':
        return { rangeStart: currentDate, rangeEnd: addDays(currentDate, 30) };
    }
  }, [view, currentDate]);

  // ── Period label ──────────────────────────────────────────────────────────

  const periodLabel = useMemo(() => {
    switch (view) {
      case 'day':
        return currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      case 'week':
        return formatWeekRange(currentDate);
      case 'month':
        return currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      case 'agenda':
        return 'Upcoming';
    }
  }, [view, currentDate]);

  // ── Load family & initial events ──────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const families = await api.getFamilies();
        if (!families?.length) { navigate('/dashboard'); return; }
        const fid = families[0].id;
        const dashboard = await api.getDashboard(fid);
        setFamilyId(fid);
        setDependents(dashboard.dependents ?? []);
        setFamilyMembers(dashboard.familyMembers ?? []);
        setActiveDepIds((dashboard.dependents ?? []).map((d: any) => d.id));
      } catch {
        navigate('/dashboard');
      }
    })();
  }, [navigate]);

  // ── Load calendar events ──────────────────────────────────────────────────

  const loadEvents = useCallback(async () => {
    if (!familyId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getCalendarEvents(
        familyId,
        isoDate(rangeStart),
        isoDate(addDays(rangeEnd, 1)),
        activeDepIds.length ? activeDepIds : undefined
      );
      setEvents(data);
    } catch {
      setError('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [familyId, rangeStart, rangeEnd, activeDepIds]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ── Navigation ────────────────────────────────────────────────────────────

  const prevPeriod = () => {
    if (view === 'day') setCurrentDate((d) => addDays(d, -1));
    else if (view === 'week') setCurrentDate((d) => addDays(d, -7));
    else if (view === 'month') setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    else setCurrentDate((d) => addDays(d, -30));
  };

  const nextPeriod = () => {
    if (view === 'day') setCurrentDate((d) => addDays(d, 1));
    else if (view === 'week') setCurrentDate((d) => addDays(d, 7));
    else if (view === 'month') setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    else setCurrentDate((d) => addDays(d, 30));
  };

  const goToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (view === 'week') setCurrentDate(getMonday(today));
    else setCurrentDate(today);
  };

  // ── When switching view, keep current date aligned ───────────────────────

  const switchView = (v: CalendarView) => {
    setView(v);
    if (v === 'week') {
      // anchor to Monday of current date's week
      setCurrentDate((d) => getMonday(d));
    } else if (v === 'month') {
      setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth(), 1));
    }
  };

  // ── Week/day days ─────────────────────────────────────────────────────────

  const weekDays = useMemo(() => {
    if (view === 'day') return [currentDate];
    // week view: Mon–Sun
    return Array.from({ length: 7 }, (_, i) => addDays(currentDate, i));
  }, [view, currentDate]);

  const todayStr = isoDate(new Date());
  const colCount = weekDays.length;

  // ── Drag helpers ─────────────────────────────────────────────────────────

  const handleDrop = async (e: React.DragEvent, targetDate: Date, relativeY: number) => {
    e.preventDefault();
    const ev = draggedEvent.current;
    if (!ev || ev.allDay) return;

    const rawMinutes = (relativeY / HOUR_HEIGHT) * 60 + HOUR_START * 60;
    const snappedMinutes = Math.round(rawMinutes / 15) * 15;
    const h = Math.floor(snappedMinutes / 60);
    const m = snappedMinutes % 60;
    const newStartTime = `${pad(h)}:${pad(m)}`;
    const duration = (() => {
      if (!ev.startTime || !ev.endTime) return 60;
      const [sh, sm] = ev.startTime.split(':').map(Number);
      const [eh, em] = ev.endTime.split(':').map(Number);
      return (eh * 60 + em) - (sh * 60 + sm);
    })();
    const newEndTime = addMinutes(newStartTime, duration);
    const newDateStr = isoDate(targetDate);

    try {
      if (ev.type === 'ACTIVITY' && ev.sessionId) {
        await api.updateActivitySession(familyId, ev.sourceId, ev.sessionId, {
          dayOfWeek: dayOfWeekName(targetDate),
          startTime: newStartTime,
          endTime: newEndTime,
        });
      } else if (ev.type === 'APPOINTMENT') {
        await api.updateAppointment(ev.sourceId, {
          dateTime: new Date(`${newDateStr}T${newStartTime}:00`).toISOString(),
        });
      } else if (ev.type === 'TASK') {
        await api.updateTask(ev.sourceId, {
          dueAt: new Date(`${newDateStr}T00:00:00`).toISOString(),
        });
      }
      draggedEvent.current = null;
      loadEvents();
    } catch {
      setError('Failed to reschedule event');
    }
  };

  // ── Group events by date ─────────────────────────────────────────────────

  const eventsByDate = new Map<string, CalendarEvent[]>();
  events.forEach((ev) => {
    const list = eventsByDate.get(ev.date) ?? [];
    list.push(ev);
    eventsByDate.set(ev.date, list);
  });

  const getTimedEvents = (date: Date) =>
    (eventsByDate.get(isoDate(date)) ?? []).filter((ev) => !ev.allDay);

  const getAllDayEvents = (date: Date) =>
    (eventsByDate.get(isoDate(date)) ?? []).filter((ev) => ev.allDay);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full bg-gray-50">
      {/* ── Navigation sub-header ────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          {/* Left: prev/next/today + title */}
          <div className="flex items-center gap-2">
            <button
              onClick={prevPeriod}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Previous period"
            >
              ‹
            </button>
            <span className="font-semibold text-gray-900 text-sm sm:text-base min-w-[160px] text-center">
              {periodLabel}
            </span>
            <button
              onClick={nextPeriod}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Next period"
            >
              ›
            </button>
            <button
              onClick={goToday}
              className="text-sm px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
            >
              Today
            </button>
          </div>

          {/* Center: view switcher */}
          <div className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
            {(['day', 'week', 'month', 'agenda'] as CalendarView[]).map((v) => (
              <button
                key={v}
                onClick={() => switchView(v)}
                className={`px-3 py-1 text-sm rounded-md capitalize transition-colors ${
                  view === v
                    ? 'bg-white shadow-sm font-medium text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          {/* Right: subscribe + add buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSubscribe(true)}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors"
              title="Subscribe via iCal"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </button>
            <button
              onClick={() => setNewSlot({ date: todayStr, time: '09:00' })}
              className="btn btn-primary text-sm"
            >
              + Add
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* ── Dependent chips ─────────────────────────────────────────── */}
        {dependents.length > 1 && (
          <div className="mb-4">
            <DependentChips
              dependents={dependents}
              activeIds={new Set(activeDepIds)}
              onToggle={(id) =>
                setActiveDepIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                )
              }
            />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-3 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* ── Month view ──────────────────────────────────────────────── */}
        {view === 'month' && (
          loading ? (
            <div className="bg-white rounded-xl border border-gray-200 h-96 flex items-center justify-center">
              <div className="text-gray-400 text-sm">Loading…</div>
            </div>
          ) : (
            <MonthView
              currentDate={currentDate}
              events={events}
              todayStr={todayStr}
              onEventClick={setSelectedEvent}
              onDayClick={(day) => {
                setCurrentDate(day);
                switchView('day');
              }}
            />
          )
        )}

        {/* ── Agenda view ─────────────────────────────────────────────── */}
        {view === 'agenda' && (
          loading ? (
            <div className="bg-white rounded-xl border border-gray-200 h-64 flex items-center justify-center">
              <div className="text-gray-400 text-sm">Loading…</div>
            </div>
          ) : (
            <AgendaView
              events={events}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              todayStr={todayStr}
              onEventClick={setSelectedEvent}
            />
          )
        )}

        {/* ── Day / Week grid ──────────────────────────────────────────── */}
        {(view === 'week' || view === 'day') && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Day header row */}
            <div
              className="grid sticky top-[57px] z-10 bg-white border-b border-gray-200"
              style={{ gridTemplateColumns: `56px repeat(${colCount}, 1fr)` }}
            >
              <div className="border-r border-gray-200" /> {/* time axis spacer */}
              {weekDays.map((day, i) => {
                const dateStr = isoDate(day);
                const isToday = dateStr === todayStr;
                return (
                  <div
                    key={dateStr}
                    className={`text-center py-2 text-sm font-medium border-r border-gray-200 last:border-r-0 ${isToday ? 'text-primary-700' : 'text-gray-600'}`}
                  >
                    {view === 'week' && <div className="text-xs text-gray-400">{DAY_LABELS[i]}</div>}
                    <div className={`text-lg font-semibold ${isToday ? 'bg-primary-600 text-white w-7 h-7 rounded-full flex items-center justify-center mx-auto' : ''}`}>
                      {day.getDate()}
                    </div>
                    {view === 'day' && (
                      <div className="text-xs text-gray-400">
                        {day.toLocaleDateString('en-GB', { weekday: 'long' })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* All-day strip */}
            <div
              className="grid border-b border-gray-200 min-h-[32px]"
              style={{ gridTemplateColumns: `56px repeat(${colCount}, 1fr)` }}
            >
              <div className="border-r border-gray-200 flex items-center justify-end pr-2">
                <span className="text-[10px] text-gray-400 leading-none">all day</span>
              </div>
              {weekDays.map((day) => {
                const dateStr = isoDate(day);
                const allDay = getAllDayEvents(day);
                return (
                  <div
                    key={dateStr}
                    className="border-r border-gray-200 last:border-r-0 p-0.5 space-y-0.5"
                    onClick={() => setNewSlot({ date: dateStr, time: '' })}
                  >
                    {allDay.map((ev) => (
                      <AllDayChip key={ev.id} event={ev} onClick={() => setSelectedEvent(ev)} />
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Timed grid */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-gray-400 text-sm">Loading…</div>
              </div>
            ) : (
              <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                <div
                  className="grid"
                  style={{ gridTemplateColumns: `56px repeat(${colCount}, 1fr)`, height: TOTAL_HEIGHT }}
                >
                  {/* Time axis */}
                  <div className="border-r border-gray-200 relative">
                    {HOURS.map((h) => (
                      <div
                        key={h}
                        className="absolute w-full flex items-start justify-end pr-2"
                        style={{ top: (h - HOUR_START) * HOUR_HEIGHT }}
                      >
                        <span className="text-[10px] text-gray-400 leading-none -mt-2">
                          {pad(h)}:00
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {weekDays.map((day) => {
                    const dateStr = isoDate(day);
                    const timedEvs = getTimedEvents(day);

                    return (
                      <div
                        key={dateStr}
                        className="border-r border-gray-200 last:border-r-0 relative"
                        style={{ height: TOTAL_HEIGHT }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          handleDrop(e, day, e.clientY - rect.top);
                        }}
                      >
                        {/* Hour lines */}
                        {HOURS.map((h) => (
                          <div
                            key={h}
                            className="absolute w-full border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                            style={{ top: (h - HOUR_START) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                            onClick={() => setNewSlot({ date: dateStr, time: `${pad(h)}:00` })}
                          />
                        ))}

                        {/* Events */}
                        {timedEvs.map((ev) => (
                          <EventCard
                            key={ev.id}
                            event={ev}
                            onDragStart={() => { draggedEvent.current = ev; }}
                            onClick={() => setSelectedEvent(ev)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Legend ──────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-100 border-l-2 border-l-green-500 inline-block" /> Activity</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-50 border-l-2 border-l-blue-500 inline-block" /> Appointment</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-50 border-l-2 border-l-amber-400 inline-block" /> Task</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-purple-50 border-l-2 border-l-purple-400 inline-block" /> Handover</span>
        </div>
      </div>

      {/* ── Panels ─────────────────────────────────────────────────────── */}
      {selectedEvent && (
        <EventDetailPanel
          event={selectedEvent}
          familyId={familyId}
          familyMembers={familyMembers}
          dependents={dependents}
          onClose={() => setSelectedEvent(null)}
          onDelete={() => { setSelectedEvent(null); loadEvents(); }}
          onSave={() => { setSelectedEvent(null); loadEvents(); }}
        />
      )}

      {newSlot && familyId && (
        <CreateEventPanel
          slot={newSlot}
          familyId={familyId}
          familyMembers={familyMembers}
          dependents={dependents}
          onSuccess={() => { setNewSlot(null); loadEvents(); }}
          onClose={() => setNewSlot(null)}
        />
      )}

      {showSubscribe && familyId && (
        <SubscribeModal
          familyId={familyId}
          onClose={() => setShowSubscribe(false)}
        />
      )}

    </div>
  );
}
